import React from "react";
import { Map, MapPinHouse } from "lucide-react";
import SearchResultCard from "./SearchResultCard";
import ResultsHeader from "./ResultsHeader";
import RelatedSearches from "./RelatedSearches";
import { useOrg } from "../../contexts/OrganizationContext";
import { useSearch } from "../../contexts/SearchContext";
import { getPageResultCount } from "../../utils/geo";

function SearchResultsList({
  results,
  totalResults,
  currentPage,
  lastPage,
  perPage,
  searchCoords,
  onSelectResult,
  selectedResultId,
  onIndicateResult,
  onRestoreResult,
  responseId,
  surveyName,
  savedResourceIds,
  onResourceSaved,
  pageNum,
  hasSearchTerms,
  onViewMap,
  relatedTerms,
  onRelatedSearch,
}) {
  const { organization } = useOrg();
  const { params } = useSearch();
  const stateName = organization.serviceLocale?.stateName || "";
  const resultCount = getPageResultCount(totalResults, perPage, currentPage, lastPage);
  const showLocationNotice = !!params.location && (!params.coords || params.service_area === stateName.toLowerCase());

  if (!hasSearchTerms && results.length === 0) {
    return (
      <div className="empty-search-instructions">
        <h4><i className="ico ico-search" /></h4>
        <h3>Enter a search term and {stateName} location in the search fields above.</h3>
        <p>
          &quot;Search a service - anything from housing to community gardens to
          volunteer opportunities to the name of a specific agency you want to
          connect with. Provide a {stateName} zip code for best results.&quot;
        </p>
        <div className="fake-cards" />
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="no-results" style={{ margin: "1em 1.5em" }}>
        <p>No results found. Try broadening your search.</p>
      </div>
    );
  }

  return (
    <div className="search-results-layout results-content">
      {showLocationNotice && (
        <div className="search-results-notice" role="status">
          <MapPinHouse aria-hidden="true" size={16} strokeWidth={1.8} />
          <span>
            Searching all of {params.location || stateName}. For best results, refine your location.
          </span>
        </div>
      )}

      <div className="results-summary">
        <h1 className="results-summary-title">Search Results</h1>
      </div>

      <div className="header-detail-region results-header-detail results-header search-results-header-row">
        <div className="search-results-header-details">
          <span className="page-result-count result-detail result-count">{resultCount}</span>
          <ResultsHeader results={results} pageNum={pageNum} />
        </div>
        <div className="search-results-header-buttons">
          <button className="search-results-view-tab search-results-map-toggle" type="button" onClick={onViewMap}>
            <Map aria-hidden="true" size={16} strokeWidth={1.8} />
            <span>View on map</span>
          </button>
        </div>
      </div>

      <div className="related-search-region">
        <RelatedSearches relatedTerms={relatedTerms} onSelect={onRelatedSearch} />
      </div>

      <div className="results-region">
        <div className="search-results">
          {results.map((result) => (
            <SearchResultCard
              key={result.program_site_id}
              result={result}
              searchCoords={searchCoords}
              isSelected={selectedResultId === result.program_site_id}
              onSelect={onSelectResult}
              onMouseEnter={onIndicateResult}
              onMouseLeave={onRestoreResult}
              responseId={responseId}
              surveyName={surveyName}
              savedResourceIds={savedResourceIds}
              onResourceSaved={onResourceSaved}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default SearchResultsList;
